import { useTranslation } from 'react-i18next';

import type { CardPreview, CardSides } from '../../../api/cards';
import { cardMessageText } from '../../../api/cards';
import type { CardResult } from '../../../types';
import { Modal } from '../../common/Modal';

export function ResultView({
  result,
  preview,
  upn,
  sides,
  onPreview,
  onReset,
}: {
  result: CardResult | null;
  preview: CardPreview | undefined;
  upn: string;
  sides: CardSides;
  onPreview: (url: string) => void;
  onReset: () => void;
}) {
  const success = result?.status === 'success';
  return (
    <div className="space-y-3">
      <ResultStatus result={result} success={success} />
      {preview && (
        <ResultPreview
          preview={preview}
          upn={upn}
          sides={sides}
          onPreview={onPreview}
          onReset={onReset}
        />
      )}
      {!preview && success && (
        <AgainButton onReset={onReset} className="text-xs transition-colors" />
      )}
    </div>
  );
}

function ResultStatus({
  result,
  success,
}: {
  result: CardResult | null;
  success: boolean;
}) {
  const { t } = useTranslation();
  const message = success
    ? t('singleCardGenerator.done')
    : (result && cardMessageText(t, result)) || t('singleCardGenerator.failed');
  return (
    <div
      className="flex items-center gap-2 text-sm"
      style={{ color: success ? '#007a47' : 'var(--magenta)' }}
    >
      <svg
        className="size-4 shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={success ? 'M5 13l4 4L19 7' : 'M6 18L18 6M6 6l12 12'}
        />
      </svg>
      {message}
    </div>
  );
}

function ResultPreview({
  preview,
  upn,
  sides,
  onPreview,
  onReset,
}: {
  preview: CardPreview;
  upn: string;
  sides: CardSides;
  onPreview: (url: string) => void;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  // Verso-only batches render a single card whose `url` IS the verso.
  const isBackOnly = sides === 'back';
  // A const, so the guard's narrowing survives into the thumb's onClick.
  const backUrl = preview.backUrl;
  const frontLabel = isBackOnly
    ? t('singleCardGenerator.back')
    : t('singleCardGenerator.front');
  let downloadLabel = t('singleCardGenerator.download');
  if (backUrl) {
    downloadLabel = t('singleCardGenerator.downloadFront');
  } else if (isBackOnly) {
    downloadLabel = t('singleCardGenerator.downloadBack');
  }

  return (
    <div className="flex flex-wrap items-start gap-3">
      <CardThumb
        url={preview.url}
        label={frontLabel}
        border={isBackOnly ? 'var(--magenta)' : 'var(--rule)'}
        showLabel={!!backUrl || isBackOnly}
        onClick={() => onPreview(preview.url)}
      />
      {backUrl && (
        <CardThumb
          url={backUrl}
          label={t('singleCardGenerator.back')}
          border="var(--magenta)"
          showLabel
          onClick={() => onPreview(backUrl)}
        />
      )}
      <div className="flex flex-col gap-2 pt-1">
        <DownloadLink
          url={preview.url}
          filename={`card-${upn}-${isBackOnly ? 'back' : 'front'}.jpg`}
          color="var(--blue)"
          label={downloadLabel}
        />
        {backUrl && (
          <DownloadLink
            url={backUrl}
            filename={`card-${upn}-back.jpg`}
            color="var(--magenta)"
            label={t('singleCardGenerator.downloadBack')}
          />
        )}
        <AgainButton
          onReset={onReset}
          className="text-left text-xs transition-colors"
        />
      </div>
    </div>
  );
}

function AgainButton({
  onReset,
  className,
}: {
  onReset: () => void;
  className: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onReset}
      className={`${className} text-muted-soft`}
    >
      {t('singleCardGenerator.again')}
    </button>
  );
}

function CardThumb({
  url,
  label,
  border,
  showLabel,
  onClick,
}: {
  url: string;
  label: string;
  border: string;
  showLabel: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-1">
      <button type="button" onClick={onClick} className="shrink-0">
        <img
          src={url}
          alt={label}
          className="h-20 w-32 cursor-zoom-in bg-card object-cover transition-colors"
          style={{ border: `1px solid ${border}` }}
        />
      </button>
      {showLabel && (
        <span
          className="text-xs"
          style={{
            color: border === 'var(--magenta)' ? border : 'var(--muted-soft)',
          }}
        >
          {label}
        </span>
      )}
    </div>
  );
}

function DownloadLink({
  url,
  filename,
  color,
  label,
}: {
  url: string;
  filename: string;
  color: string;
  label: string;
}) {
  return (
    <a
      href={url}
      download={filename}
      className="flex items-center gap-1.5 text-xs font-medium transition-colors"
      style={{ color }}
    >
      <svg
        className="size-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
        />
      </svg>
      {label}
    </a>
  );
}

export function PreviewModal({
  url,
  onClose,
}: {
  url: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      aria-label={t('cardGenerator.results.previewTitle')}
      onClose={onClose}
      className="backdrop:bg-ink/70"
    >
      <div className="relative w-full max-w-2xl">
        <img
          src={url}
          alt={t('cardGenerator.results.previewTitle')}
          className="w-full bg-card"
        />
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={onClose}
          className="absolute -top-3 -right-3 flex size-7 items-center justify-center border border-ink bg-card text-lg leading-none text-ink"
        >
          ×
        </button>
      </div>
    </Modal>
  );
}
